/**
 * Quando o convite para ligar os avisos aparece (#674).
 *
 * ── O defeito ────────────────────────────────────────────────────────────
 *
 * A permissão de notificação é do navegador POR ENDEREÇO: a dada ao staging
 * não vale na produção, e todo navegador novo começa em "perguntar". O único
 * lugar para ligar era o interruptor dentro do atendimento — quem não abre o
 * painel nunca descobre, e conclui que "o CRM parou de avisar".
 *
 * ── Quem vê ──────────────────────────────────────────────────────────────
 *
 * Quem recebe notificação (o papel lê conversa), com a permissão do navegador
 * ainda em "perguntar" e sem ter escolhido os avisos fora da tela no
 * controle. Com a permissão BLOQUEADA, o convite vira explicação de onde
 * liberar — sem botão de ativar, que não funcionaria. Navegador sem a API, ou
 * permissão já dada: nada a convidar.
 *
 * ── "Agora não" é por pessoa, e volta ────────────────────────────────────
 *
 * Some por um dia e volta (decidido em 22/10/2026). A chave leva o
 * ID de quem adiou — no computador do balcão, o adiamento de uma pessoa não
 * esconde o convite da próxima. É um ID, nunca nome nem e-mail.
 */

import type { PermissaoDoAviso } from './aviso-do-sistema';

export type SituacaoDoConvite = 'nenhum' | 'convidar' | 'bloqueado';

/** Um dia. */
export const ADIAMENTO_DO_CONVITE_MS = 24 * 60 * 60 * 1000;

function chave(usuarioId: string): string {
  return `cliqcrm:convite-dos-avisos-ate:${usuarioId}`;
}

export function situacaoDoConvite(entrada: {
  readonly recebeNotificacao: boolean;
  readonly permissao: PermissaoDoAviso;
  readonly decidida: boolean;
  readonly adiadoAte: number;
  readonly agora: number;
}): SituacaoDoConvite {
  if (!entrada.recebeNotificacao || entrada.decidida) return 'nenhum';
  if (entrada.agora < entrada.adiadoAte) return 'nenhum';
  if (entrada.permissao === 'default') return 'convidar';
  if (entrada.permissao === 'denied') return 'bloqueado';
  return 'nenhum';
}

/**
 * Até quando o convite está adiado, em epoch. `0` = não está. Valor
 * estragado, ou mais longe que um adiamento (relógio errado), vale `0`.
 */
export function lerAdiamentoDoConvite(
  usuarioId: string,
  deposito: Storage | undefined = globalThis.localStorage,
  agora: number = Date.now(),
): number {
  try {
    const ate = Number(deposito?.getItem(chave(usuarioId)) ?? 0);
    if (!Number.isFinite(ate) || ate <= 0 || ate > agora + ADIAMENTO_DO_CONVITE_MS) return 0;
    return ate;
  } catch {
    return 0;
  }
}

/** Adia por um dia e devolve até quando. Sem armazenamento, vale só nesta aba. */
export function adiarConvite(
  usuarioId: string,
  deposito: Storage | undefined = globalThis.localStorage,
  agora: number = Date.now(),
): number {
  const ate = agora + ADIAMENTO_DO_CONVITE_MS;
  try {
    deposito?.setItem(chave(usuarioId), String(ate));
  } catch {
    // Quem chamou guarda o valor em memória: a faixa some nesta aba.
  }
  return ate;
}
